/**
 * File Encoding Utilities
 * Handles binary file data conversion to/from formats suitable for transmission
 */

/**
 * Convert File object to base64 string
 * WHY: Base64 encoding allows binary data to be transmitted as JSON text
 * USAGE: For smaller files (< 5MB) when you want to transmit as JSON
 * 
 * @param {File} file - File object from input element
 * @returns {Promise<string>} - Base64 encoded file contents
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      // reader.result is data URL like "data:application/json;base64,eyJrZXk6..."
      // Extract just the base64 part after the comma
      const base64String = reader.result.split(',')[1];
      resolve(base64String);
    };
    
    reader.onerror = (error) => {
      reject(new Error(`Failed to read file: ${error.message}`));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Convert File object to ArrayBuffer
 * WHY: ArrayBuffer is raw binary data, efficient for transmission or processing
 * USAGE: For chunked uploads or when creating FormData
 * 
 * @param {File} file - File object from input element
 * @returns {Promise<ArrayBuffer>} - Binary file contents
 */
export const fileToArrayBuffer = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      resolve(reader.result);
    };
    
    reader.onerror = (error) => {
      reject(new Error(`Failed to read file: ${error.message}`));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Convert File object to Blob (essentially returns file as-is)
 * WHY: Blob is the base type for File, useful for FormData or direct transmission
 * USAGE: When you need the raw binary without conversion
 * 
 * @param {File} file - File object from input element
 * @returns {Blob} - File as Blob object
 */
export const fileToBlob = (file) => {
  return file; // File extends Blob, so this is the raw binary
};

/**
 * Create FormData object from files
 * WHY: FormData enables multipart/form-data transmission with binary data
 * USAGE: For file uploads via multipart requests
 * 
 * ⚠️ MULTI-FILE FIX:
 * - Properly tracks which files were actually added (not skipped)
 * - Ensures metadata indices align with added files (fileMetadata[0], [1], [2]...)
 * - Prevents metadata misalignment when files are skipped due to validation
 * - Validates all files before FormData creation
 * 
 * @param {Array<File>} files - Array of File objects
 * @param {Object} additionalData - Additional form fields {key: value}
 * @returns {FormData} - FormData object ready to POST
 */
export const createFormData = (files, additionalData = {}) => {
  const formData = new FormData();
  
  // Add all additional data fields
  Object.entries(additionalData).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      // Convert objects to JSON strings
      const serialValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
      formData.append(key, serialValue);
    }
  });
  
  // MULTI-FILE FIX: Track how many files were successfully added
  // WHY: Need separate index for metadata vs original index
  // Example: if file[0] is valid and file[1] is skipped, then file[2]'s metadata should be [1], not [2]
  let addedFileCount = 0;
  const skippedFiles = [];
  
  // Add files with their metadata
  files.forEach((fileWrapper, originalIndex) => {
    try {
      // Extract the actual File object - it may be wrapped or unwrapped
      // WHY: useFileUpload wraps files as {file, preview, name, size, ...}
      // But files could also be raw File objects passed directly
      const actualFile = fileWrapper.file instanceof File ? fileWrapper.file : fileWrapper;
      
      // Verify it's a real File or Blob before appending
      // WHY: This double-checks that file is valid (defensive programming)
      if (!(actualFile instanceof File || actualFile instanceof Blob)) {
        console.warn(
          `[FormData] Skipping invalid file at index ${originalIndex}:`,
          `Type is ${typeof actualFile}, expected File or Blob`,
          fileWrapper
        );
        skippedFiles.push({
          index: originalIndex,
          reason: 'Not a File or Blob instance',
          name: fileWrapper.name || 'unknown',
        });
        return;
      }
      
      // Additional validation: check for zero-byte files
      if (actualFile.size === 0) {
        console.warn(`[FormData] Skipping empty file at index ${originalIndex}: ${fileWrapper.name}`);
        skippedFiles.push({
          index: originalIndex,
          reason: 'File is empty (0 bytes)',
          name: fileWrapper.name || 'unknown',
        });
        return;
      }
      
      // Extract metadata - prefer wrapped properties, fall back to File properties
      const name = fileWrapper.name || actualFile.name || `file_${originalIndex}`;
      const type = fileWrapper.type || actualFile.type || 'application/octet-stream';
      const size = fileWrapper.size || actualFile.size || 0;
      const category = fileWrapper.category || 'unknown';
      const extension = fileWrapper.extension || '';
      
      // MULTI-FILE FIX: Use addedFileCount for metadata indices, not originalIndex
      // This ensures sequential indices even if files are skipped
      const metadataIndex = addedFileCount;
      
      // Append the actual File/Blob object to FormData
      // WHY: Same form field name 'files' for all files enables array-like handling in backend
      // Backend receives this as req.files array or form.File["files"] array
      formData.append(`files`, actualFile, name);
      
      // Add metadata for each file in separate fields using correct index
      formData.append(`fileMetadata[${metadataIndex}].name`, name);
      formData.append(`fileMetadata[${metadataIndex}].category`, category);
      formData.append(`fileMetadata[${metadataIndex}].type`, type);
      formData.append(`fileMetadata[${metadataIndex}].size`, size);
      formData.append(`fileMetadata[${metadataIndex}].extension`, extension);
      
      console.log(
        `[FormData] Added file: ${name} (${size} bytes, type: ${type}) at metadata index [${metadataIndex}]`
      );
      
      addedFileCount++;
      
    } catch (err) {
      // WHY: Catch unexpected errors during FormData append
      console.error(
        `[FormData] Unexpected error adding file at index ${originalIndex}:`,
        err,
        fileWrapper
      );
      skippedFiles.push({
        index: originalIndex,
        reason: err.message,
        name: fileWrapper?.name || 'unknown',
      });
    }
  });
  
  // MULTI-FILE FIX: Log summary for debugging
  console.log(
    `[FormData] Summary: Added ${addedFileCount}/${files.length} files`,
    skippedFiles.length > 0 ? `(${skippedFiles.length} skipped)` : ''
  );
  
  if (skippedFiles.length > 0) {
    console.warn('[FormData] Skipped files:', skippedFiles);
  }
  
  // MULTI-FILE FIX: Add count of files to FormData for backend reference
  // WHY: Backend can verify it received expected number of files
  formData.append('totalFiles', String(addedFileCount));
  
  return formData;
};

/**
 * Calculate SHA-256 hash of file for integrity verification
 * WHY: Hash allows backend to verify file wasn't corrupted during transmission
 * USAGE: Send hash to backend, backend compares after receiving file
 * 
 * @param {File} file - File object from input element
 * @returns {Promise<string>} - Hex string of SHA-256 hash
 */
export const calculateFileHash = async (file) => {
  const buffer = await fileToArrayBuffer(file);
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  
  // Convert ArrayBuffer to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
};

/**
 * Format bytes to human-readable size
 * WHY: Display file sizes in UI (e.g., "2.5 MB" instead of "2621440")
 * USAGE: For progress indicators and file list displays
 * 
 * @param {number} bytes - File size in bytes
 * @param {number} decimals - Decimal places to display
 * @returns {string} - Human-readable size (e.g., "2.5 MB")
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * Math.pow(10, decimals)) / Math.pow(10, decimals) + ' ' + sizes[i];
};

const fileEncodingUtils = {
  fileToBase64,
  fileToArrayBuffer,
  fileToBlob,
  createFormData,
  calculateFileHash,
  formatFileSize,
};

export default fileEncodingUtils;
